/**
 * What to ask for, per ticket type — Step 199.
 *
 * Kai: *"the create change form bits don't really make sense 'explain what is
 * wrong' doesn't really apply to a change, more an incident? Check across
 * other types too."* He is right, and it was wrong for four of the six types:
 * the form asked *"What's wrong, in one line"* and *"What happened?"*
 * regardless of what was being raised.
 *
 * That is not cosmetic. The prompt is the only instruction most people read,
 * and asking what is wrong on a change invites a description of a problem
 * rather than a description of the work — which is then what the approvers
 * get, and what the implementer has to work from at 22:00 on a Tuesday.
 *
 * Shared rather than local to the form because the agent side, the portal and
 * the template editor all ask the same question and must ask it the same way.
 * Unknown types fall back to the incident wording, which is the commonest and
 * the one the product started with.
 */
export interface TicketPrompt {
  /** Placeholder for the one-line subject. */
  subject: string;
  /** Placeholder for the description body. */
  body: string;
}

const INCIDENT: TicketPrompt = {
  subject: "What's wrong, in one line",
  body: 'What happened? What did you expect instead, and when did it start?',
};

export const TICKET_PROMPTS: Record<string, TicketPrompt> = {
  incident: INCIDENT,
  major_incident: {
    subject: 'What is down, in one line',
    body: 'What is affected, who can see it, and what is known so far?',
  },
  service_request: {
    subject: 'What do you need, in one line',
    body: 'What are you asking for, who is it for, and when do you need it by?',
  },
  problem: {
    subject: 'What keeps happening, in one line',
    body: 'What is the recurring symptom, which tickets does it explain, and what is known about the cause?',
  },
  change: {
    subject: 'What is changing, in one line',
    body: 'What is being changed, on what, and why now? The plan, the test and the backout are asked for separately.',
  },
  task: {
    subject: 'What needs doing, in one line',
    body: 'What has to be done, and is there anything the person picking it up needs to know first?',
  },
};

export function ticketPrompt(type: string | null | undefined): TicketPrompt {
  return TICKET_PROMPTS[type ?? ''] ?? INCIDENT;
}

/**
 * The types a TEMPLATE may raise — Step 199.
 *
 * All of them. The API enum was `incident | service_request`, which is the
 * whole reason change templates did not exist: the form offered two options
 * and the server refused the rest.
 *
 * Here rather than in the route so the editor's dropdown and the validator
 * cannot disagree — they did, once, in the other direction (the editor
 * produced an assignee value the API rejected), and the fix was the same:
 * one list, both sides.
 */
export const TEMPLATE_TYPES = [
  'incident',
  'service_request',
  'change',
  'problem',
  'task',
  'major_incident',
] as const;
